// lib
import { TypedPool } from './InsightsDashboardPool.types'
import PoolSagaStream from '@duckness/pool-saga-stream'
import { checkPortalType } from '@aytm/app/insights/dashboard/lib/config'

// stores
import staticStore from '@aytm/app/insights/dashboard/stores/static.store'

// models
import DashboardSchemaModel from '@aytm/app/insights/dashboard/models/DashboardSchemaModel'

// ducks
import { AccessKeyDuck, ACCESS_KEY_ACTIONS } from './ducks/AccessKeyDuck'
import { DashboardDuck, DASHBOARD_ACTIONS } from './ducks/DashboardDuck'
import { DataDuck, DATA_ACTIONS } from './ducks/DataDuck'
import { EventDuck, EVENT_ACTIONS } from './ducks/EventDuck'
import { FiltersDuck, FILTERS_ACTIONS } from './ducks/FiltersDuck'
import { PortalDuck, PORTAL_ACTIONS } from './ducks/PortalDuck'
import { EditorDuck, EDITOR_ACTIONS } from './ducks/EditorDuck'
import { ExportDuck, EXPORT_ACTIONS } from './ducks/ExportDuck'
import { ErrorDuck, ERROR_ACTIONS } from './ducks/ErrorDuck'
import { WelcomeDuck, WELCOME_ACTIONS } from './ducks/WelcomeDuck'

import {
  ObsoleteStorageKeys,
  StorageKeys,
  retrieveKeyedValue,
  storeKeyedValue,
  removeValue
} from '@aytm/app/insights/dashboard/lib/storage'

// schema control
import { reconcileSchemas } from './schemaControl'

import { safelyExecuteSync } from '@aytm/app/insights/dashboard/lib/safety'
import getBlockModelByType from '@aytm/app/insights/dashboard/models/blocks'
import { ReadonlyViewMiddleware } from './middlewares'
import { updatePortalModified } from '../helpers/requests'

const applyConcurrencyPolicy = (currentSchema, remoteSchema) => {
  let synchronizedSchema = currentSchema
  let changed = false

  /**
   * Add blocks added by another workspace
   */
  for (const remoteBlock of remoteSchema?.get('blocks')?.values() || []) {
    const currentBlock = synchronizedSchema?.getIn(['blocks', remoteBlock.get('uid')])
    if (!currentBlock) {
      synchronizedSchema = synchronizedSchema.addBlock(remoteBlock)
      changed = true
    }
  }

  /**
   * Delete blocks deleted by another workspace
   */

  for (const currentBlock of synchronizedSchema?.get('blocks')?.values() || []) {
    const remoteBlock = remoteSchema?.getIn(['blocks', currentBlock.get('uid')])
    if (!remoteBlock) {
      synchronizedSchema = synchronizedSchema.deleteIn(['blocks', currentBlock.get('uid')])
      changed = true
    }
  }

  /**
   * Correct default schema uid
   */

  // Get current default block uid
  const currentBlockUID = synchronizedSchema.get('currentBlockUID')

  // Get all default block uids
  const defaultBlockUIDs =
    synchronizedSchema
      .get('blocks')
      ?.filter(block => 'portal' === block.purpose)
      ?.map(block => block.get('uid'))
      ?.toArray() || []

  // Try to detect correct current block uid
  const synchronizedCurrentBlockUID =
    currentBlockUID ||
    (defaultBlockUIDs.length > 0 ? defaultBlockUIDs[0] : synchronizedSchema.getIn(['blocks', 0, 'uid']))

  // Correct schema
  if (synchronizedCurrentBlockUID) {
    // Correct schema current block uid
    if (currentBlockUID !== synchronizedCurrentBlockUID) {
      synchronizedSchema = synchronizedSchema.set('currentBlockUID', synchronizedCurrentBlockUID)
      changed = true
    }
    // Correct blocks. Leave only one default block
    if (1 !== defaultBlockUIDs.length || currentBlockUID !== synchronizedCurrentBlockUID) {
      synchronizedSchema = synchronizedSchema.update('blocks', blocks =>
        blocks.map(block =>
          synchronizedCurrentBlockUID === block.get('uid')
            ? block.set('purpose', 'portal')
            : block.set('purpose', 'view')
        )
      )
      changed = true
    }
  }

  // Synchronize schema patches (patchedAt, groups, filters)
  if (remoteSchema.get('patchedAt') && synchronizedSchema.get('patchedAt') !== remoteSchema.get('patchedAt')) {
    synchronizedSchema = synchronizedSchema.set('patchedAt', remoteSchema.get('patchedAt'))
    synchronizedSchema = synchronizedSchema.set('groups', remoteSchema.get('groups'))
    synchronizedSchema = synchronizedSchema.set('filters', remoteSchema.get('filters'))
  }

  if (remoteSchema.get('timeZone')) {
    synchronizedSchema = synchronizedSchema.set('timeZone', remoteSchema.get('timeZone'))
  }

  // Return result
  return {
    schema: synchronizedSchema,
    changed
  }
}

// Pool
/**
 */
const InsightsDashboardPool = TypedPool({
  poolName: 'insights-dashboard',
  buildStore:
    /**
     * Initializes static store.
     * Creates dashboard store.
     */
    ({ dashboard, urls, canUpdateDashboard, userFullName, accountType, ...rest } = {}) => {
      // Set static storage
      staticStore.dashboardID = dashboard?.id
      staticStore.dashboardType = checkPortalType(dashboard?.defaultType)
      staticStore.urls = urls
      if (!urls) {
        // eslint-disable-next-line no-console
        console.warn('No API endpoints provided. The application is temporarily non-functional')
        // Beyond this point, the execution is pointless, if no urls is provided.
        // TODO: Provision reporting/communication of concerns
      }
      staticStore.canUpdateDashboard = canUpdateDashboard
      staticStore.userFullName = userFullName
      staticStore.accountType = accountType
      staticStore.currentSharedKey = dashboard?.currentSharedKey
      staticStore.isSharedView = Boolean(staticStore.currentSharedKey)
      staticStore.BlockModel = getBlockModelByType(staticStore.dashboardType)

      // TODO: Rearrange code once this feature is fully deployed, replace all logic in favor of '=true'
      if (
        rest.featureMultiMetrics ||
        dashboard?.featureMultiMetrics ||
        /multimetrics=1/i.test(window.document.cookie)
      ) {
        staticStore.featureMultiMetrics = !/multimetrics=no/i.test(window.document.cookie)
        // This to facilitate prod-testing
        // eslint-disable-next-line no-console
        console.log('Feature `multiple metrics` is ' + (staticStore.featureMultiMetrics ? 'on' : 'off'))
      }

      // Initialize local schema
      ObsoleteStorageKeys.forEach(key => removeValue(key))
      const shareKeys = dashboard.sharedKeys || []
      /**
       * @type {import('./ducks/types').DashboardPersistedSchema}
       */
      const pageBakedSchema = safelyExecuteSync(() => JSON.parse(dashboard?.schema ?? 'null'), null)
      if (!pageBakedSchema) {
        // eslint-disable-next-line no-console
        console.warn('Dashboard schema is invalid or empty, initializing empty schema')
      }
      /**
       * Get schema from local storage
       * @type {import('./ducks/types').DashboardPersistedSchema}
       */
      const locallyStoredSchema = retrieveKeyedValue(
        staticStore.isSharedView ? StorageKeys.DASHBOARD_SCHEMA_SHARED : StorageKeys.DASHBOARD_SCHEMA,
        (dashboard?.id ?? '_') + (staticStore.isSharedView ? '_' + staticStore.currentSharedKey : ''),
        null
      )
      // Detect schema source remote or local
      // If no local schema found - take remote schema
      // otherwise work with local schema
      const schemaSource = pageBakedSchema
        ? staticStore.isSharedView
          ? pageBakedSchema
          : locallyStoredSchema || pageBakedSchema
        : {}

      if (!schemaSource.activeTabID) {
        schemaSource.activeTabID = 'portal'
      }

      const [currentSchemaSource, doesSchemaNeedSaving] = reconcileSchemas(pageBakedSchema, locallyStoredSchema)
      // Initialize current portal schema
      // const [currentSchema, remoteSchema, isChanged] = reconcileSchemas(
      const currentSchema = safelyExecuteSync(
        () =>
          Object.keys(currentSchemaSource).length
            ? DashboardSchemaModel.of(currentSchemaSource)
            : DashboardSchemaModel.empty(),
        DashboardSchemaModel.empty()
      )
      const remoteSchema = safelyExecuteSync(
        () =>
          Object.keys(pageBakedSchema).length ? DashboardSchemaModel.of(pageBakedSchema) : DashboardSchemaModel.empty(),
        DashboardSchemaModel.empty()
      )

      // Apply concurrency policy to schema
      const applyPolicyResult = applyConcurrencyPolicy(currentSchema, remoteSchema)

      // Update local schema
      if (!locallyStoredSchema || staticStore.isSharedView || applyPolicyResult.changed || doesSchemaNeedSaving) {
        storeKeyedValue(
          staticStore.isSharedView ? StorageKeys.DASHBOARD_SCHEMA_SHARED : StorageKeys.DASHBOARD_SCHEMA,
          dashboard?.id ?? '_',
          applyPolicyResult.schema
        )
      }

      /**
       * @type {import('@aytm/app/insights/dashboard/duckness/ducks/types').InsightsDashboardPoolState}
       */
      let state = {
        // Dashboard store
        error: null,
        title: dashboard?.title,
        currentSchema: applyPolicyResult.schema,
        remoteSchema,
        shareKeys,
        // Component store
        currentData: {
          attributes: null
        },
        // Request chain
        requestChain: {
          currentIndex: -1,
          progress: 0,
          queue: [
            { key: 'schema', status: 'pending' },
            { key: 'dataHashes', status: 'pending' },
            { key: 'dataItems', status: 'pending' },
            { key: 'statHashes', status: 'pending' },
            { key: 'statItems', status: 'pending' }
          ]
        },
        // Fresh loading flag
        isLoading: false,
        isFreshLoading: true,
        isSharedView: staticStore.isSharedView || false,
        editor: null,
        exporting: null,
        isNewSchemaVersionDetected: false // TODO: e.g. <= applyPolicyResult.changed
      }

      state = updatePortalModified(state)

      // Return dashboard store
      /**
       * @type {import('@aytm/app/insights/dashboard/duckness/ducks/types').InsightsDashboardPoolState}
       */
      return state
    }
})

InsightsDashboardPool.addStream(PoolSagaStream())

InsightsDashboardPool.addDuck(AccessKeyDuck)
InsightsDashboardPool.addDuck(DashboardDuck)
InsightsDashboardPool.addDuck(DataDuck)
InsightsDashboardPool.addDuck(PortalDuck)
InsightsDashboardPool.addDuck(FiltersDuck)
InsightsDashboardPool.addDuck(EventDuck)
InsightsDashboardPool.addDuck(EditorDuck)
InsightsDashboardPool.addDuck(ExportDuck)
InsightsDashboardPool.addDuck(ErrorDuck)
InsightsDashboardPool.addDuck(WelcomeDuck)

InsightsDashboardPool.addMiddleware(ReadonlyViewMiddleware)

export {
  AccessKeyDuck,
  DashboardDuck,
  DataDuck,
  PortalDuck,
  FiltersDuck,
  EventDuck,
  EditorDuck,
  ExportDuck,
  ErrorDuck,
  WelcomeDuck,
  ACCESS_KEY_ACTIONS,
  DASHBOARD_ACTIONS,
  DATA_ACTIONS,
  PORTAL_ACTIONS,
  FILTERS_ACTIONS,
  EVENT_ACTIONS,
  EDITOR_ACTIONS,
  EXPORT_ACTIONS,
  ERROR_ACTIONS,
  WELCOME_ACTIONS
}

export default InsightsDashboardPool

if (module.hot) {
  // reload page on pool changes (recreates pool)
  module.hot.decline()
}
